import { API_ROOT } from "./requests";

// Refresh a bit before the real expiry so a client clock that runs behind the
// server does not send tokens the server already considers expired.
const EXPIRY_SKEW_MS = 60 * 1000;
const REFRESH_LOCK_NAME = 'surftms-token-refresh';

/** The session can't be recovered (refresh token missing, expired or rejected); the user has to log in again. */
export class AuthExpiredError extends Error {
    constructor(message = 'Session expired') {
        super(message);
        this.name = 'AuthExpiredError';
    }
}

function decodeTokenPayload(token) {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
}

function tokenExpiryMs(token) {
    try {
        const { exp } = decodeTokenPayload(token);
        return exp ? exp * 1000 : 0;
    } catch {
        return 0;
    }
}

export const isTokenExpired = (token) => Date.now() > tokenExpiryMs(token);

export const tokenExpiresSoon = (token) => Date.now() > tokenExpiryMs(token) - EXPIRY_SKEW_MS;

/** Milliseconds until the current access token should be refreshed (0 if already due). */
export function msUntilRefreshDue() {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) return 0;
    return Math.max(0, tokenExpiryMs(accessToken) - EXPIRY_SKEW_MS - Date.now());
}

async function requestNewTokens(refreshToken) {
    const res = await fetch(API_ROOT + 'auth/refresh_token', {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${refreshToken}`,
        }
    });
    if (res.status === 401 || res.status === 403 || res.status === 423) {
        throw new AuthExpiredError('Refresh rejected');
    }
    if (!res.ok) {
        throw new Error('Refresh failed with status ' + res.status);
    }
    const data = await res.json();
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    return data;
}

let refreshInProgress = null;

/**
 * Gets a new token pair. `rejectedAccessToken` is the token that turned out to be
 * stale; if another tab already replaced it with a fresh one, that one is reused.
 *
 * Tabs share localStorage but not memory, so the refresh is serialized across tabs
 * with the Web Locks API; otherwise two tabs would rotate the same refresh token.
 * Rejects with AuthExpiredError when the session is gone, or a plain Error on
 * network/server failures (worth retrying, not a reason to log out).
 */
export function refreshAccessToken(rejectedAccessToken = localStorage.getItem('access_token')) {
    if (refreshInProgress) return refreshInProgress;

    const run = async () => {
        const current = localStorage.getItem('access_token');
        if (current && current !== rejectedAccessToken && !tokenExpiresSoon(current)) {
            return { access_token: current, refresh_token: localStorage.getItem('refresh_token') };
        }
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken || isTokenExpired(refreshToken)) {
            throw new AuthExpiredError('No valid refresh token');
        }
        return requestNewTokens(refreshToken);
    };

    refreshInProgress = (navigator.locks ? navigator.locks.request(REFRESH_LOCK_NAME, run) : run())
        .finally(() => {
            refreshInProgress = null;
        });
    return refreshInProgress;
}

/** Current access token, refreshed first if it is expired or about to expire. */
export async function getValidAccessToken() {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken && !tokenExpiresSoon(accessToken)) return accessToken;
    const data = await refreshAccessToken(accessToken);
    return data.access_token;
}
